<template>
  <div class="filters-container">
    <div class="filters-row">
      <div class="filters-section-1">
        <p class="font-medium">Filtrar resultados</p>
        <Dropdown
          v-model="selectedField"
          :options="filtersFields"
          optionLabel="name"
          key="name"
          placeholder="Seleccionar tipo de filtro"
          style="border-radius: 6px"
          class="custom-focus w-full"
        />
      </div>

      <div class="filters-section-2">
        <template v-if="inputType === 'text'">
          <InputText
            v-model="selectedValue"
            placeholder="Ingrese valor"
            style="border-radius: 6px"
            class="custom-focus w-full"
            @keyup.enter="handleFilter"
          />
        </template>

        <template v-else-if="inputType === 'dropdown'">
          <Dropdown
            v-model="selectedValue"
            :options="selectedField?.dependenciaOptions || []"
            optionLabel="dependencia"
            optionValue="id"
            placeholder="Seleccionar dependencia"
            style="border-radius: 6px"
            class="custom-focus w-full"
          />
        </template>

        <template v-else-if="inputType === 'date'">
          <div class="flex flex-column gap-2 w-full">
            <Dropdown
              v-model="selectedDateField"
              :options="selectedField?.dateFieldOptions || []"
              optionLabel="name"
              optionValue="value"
              placeholder="Seleccionar tipo de fecha"
              style="border-radius: 6px"
              class="custom-focus w-full"
            />
            <div class="flex justify-content-center align-items-end gap-2">
              <Calendar
                v-model="filtros.fechaDesde"
                placeholder="Desde"
                dateFormat="dd-mm-yy"
                class="w-12rem custom-focus"
                style="border-radius: 6px"
                :maxDate="hoy"
              />
              <p>-</p>
              <Calendar
                v-model="filtros.fechaHasta"
                placeholder="Hasta"
                dateFormat="dd-mm-yy"
                class="w-12rem custom-focus"
                style="border-radius: 6px !important"
                :maxDate="hoy"
              />
            </div>
          </div>
        </template>
      </div>

      <div class="filters-section-3">
        <Button class="btn btn-primary" @click="handleFilter">
          <div>
            <Search color="white" :size="20" />
          </div>
          Buscar
        </Button>
        <Button class="btn btn-outline-primary" @click="cleanFilters">
          <div>
            <BrushCleaning color="#0B5195" :size="20" />
          </div>
          Limpiar
        </Button>
      </div>
    </div>

    <div class="filters-chips" v-if="visibleFilters.length">
      <div
        v-for="filtro in visibleFilters"
        :key="filtro.key"
        class="p-chip flex align-items-center justify-content-between"
        style="background-color: #eff6fb; color: #0b5195; padding: 6px 12px; border-radius: 9999px"
      >
        {{ filtro.label }}: {{ filtro.displayValue }}
        <CircleX
          :size="20"
          color="red"
          class="ml-2 cursor-pointer"
          @click="removeFilter(filtro.key)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { BrushCleaning, Search, CircleX } from "lucide-vue";
import { useFiltersDetenidos } from "@/composables/index.js";

const props = defineProps({
  appliedFilters: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["filtrarDetenidos"]);

const { filtersFields } = useFiltersDetenidos();

const hoy = new Date();
const selectedField = ref(null);
const selectedValue = ref("");
const selectedDateField = ref(null);
const filtros = ref({
  fechaDesde: null,
  fechaHasta: null,
});
const activeFilters = ref({});

const inputType = computed(() => selectedField.value?.type || "text");
const visibleFilters = computed(() =>
  Object.values(activeFilters.value).filter((filtro) => filtro.visible !== false)
);

const formatDate = (date, withEndOfDay = false) => {
  if (!date) return null;
  const parsed = new Date(date);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  const suffix = withEndOfDay ? "23:59:59" : "00:00:00";
  return `${year}-${month}-${day} ${suffix}`;
};

const formatDateDisplay = (date) => {
  if (!date) return "";
  const parsed = new Date(date);
  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const year = parsed.getFullYear();
  return `${day}/${month}/${year}`;
};

const buildFiltersPayload = () => {
  const payload = {
    search: {},
    direct: {},
  };

  Object.values(activeFilters.value).forEach((filtro) => {
    if (filtro.queryType === "search") {
      if (Array.isArray(filtro.value)) {
        payload.search[filtro.field] = filtro.value;
      } else {
        if (!payload.search[filtro.field]) {
          payload.search[filtro.field] = [];
        }
        payload.search[filtro.field].push({
          operator: filtro.operator || "=",
          value: filtro.value,
        });
      }
    }

    if (filtro.queryType === "direct") {
      payload.direct[filtro.field] = filtro.value;
    }
  });

  if (Object.keys(payload.search).length === 0) {
    delete payload.search;
  }
  if (Object.keys(payload.direct).length === 0) {
    delete payload.direct;
  }

  return payload;
};

const updateSearchFilter = () => {
  if (!selectedField.value) return;

  const field = selectedField.value;

  if (field.type === "dropdown") {
    const value = selectedValue.value;
    if (!value) {
      if (activeFilters.value[field.field]) {
        const copy = { ...activeFilters.value };
        delete copy[field.field];
        activeFilters.value = copy;
      }
      return;
    }

    const selectedDependencia = field.dependenciaOptions?.find((opt) => opt.id === value);
    const displayValue = selectedDependencia?.dependencia || value;

    const newFilter = {
      key: field.field,
      label: field.name,
      field: field.field,
      operator: field.operator || "=",
      value,
      queryType: field.queryType,
      displayValue: displayValue,
      visible: true,
    };
    activeFilters.value = { ...activeFilters.value, [field.field]: newFilter };
  } else if (field.queryType === "date") {
    const dateKey = `date_${selectedDateField.value || ""}`;
    if (!selectedDateField.value || (!filtros.value.fechaDesde && !filtros.value.fechaHasta)) {
      if (activeFilters.value[dateKey]) {
        const copy = { ...activeFilters.value };
        delete copy[dateKey];
        activeFilters.value = copy;
      }
      return;
    }

    const operations = [];
    if (filtros.value.fechaDesde) {
      operations.push({ operator: ">=", value: formatDate(filtros.value.fechaDesde) });
    }
    if (filtros.value.fechaHasta) {
      operations.push({ operator: "<=", value: formatDate(filtros.value.fechaHasta, true) });
    }

    const selectedDateFieldOption = field.dateFieldOptions?.find(
      (option) => option.value === selectedDateField.value
    );

    let displayValue = "";
    const desdeFormatted = formatDateDisplay(filtros.value.fechaDesde);
    const hastaFormatted = formatDateDisplay(filtros.value.fechaHasta);

    if (desdeFormatted && hastaFormatted) {
      displayValue = `Desde ${desdeFormatted} Hasta ${hastaFormatted}`;
    } else if (desdeFormatted) {
      displayValue = `Desde ${desdeFormatted}`;
    } else if (hastaFormatted) {
      displayValue = `Hasta ${hastaFormatted}`;
    }

    const newFilter = {
      key: `date_${selectedDateField.value}`,
      label: selectedDateFieldOption?.name || field.name,
      field: selectedDateField.value,
      operator: null,
      value: operations,
      queryType: "search",
      displayValue: displayValue,
      visible: true,
    };
    activeFilters.value = {
      ...activeFilters.value,
      [`date_${selectedDateField.value}`]: newFilter,
    };
  } else {
    const value = `${selectedValue.value || ""}`.trim();
    if (!value) {
      if (activeFilters.value[field.field]) {
        const copy = { ...activeFilters.value };
        delete copy[field.field];
        activeFilters.value = copy;
      }
      return;
    }

    const newFilter = {
      key: field.field,
      label: field.name,
      field: field.field,
      operator: field.operator || "=",
      value,
      queryType: field.queryType,
      displayValue: value,
      visible: true,
    };
    activeFilters.value = { ...activeFilters.value, [field.field]: newFilter };
  }
};

const handleFilter = () => {
  updateSearchFilter();
  emit("filtrarDetenidos", buildFiltersPayload());
};

const cleanFilters = () => {
  selectedField.value = null;
  selectedValue.value = "";
  selectedDateField.value = null;
  filtros.value.fechaDesde = null;
  filtros.value.fechaHasta = null;
  activeFilters.value = {};
  emit("filtrarDetenidos", {});
};

const removeFilter = (key) => {
  const copy = { ...activeFilters.value };
  delete copy[key];
  activeFilters.value = copy;
  emit("filtrarDetenidos", buildFiltersPayload());
};

watch(
  () => props.appliedFilters,
  (newFilters) => {
    if (newFilters && Object.keys(newFilters).length > 0) {
      const enrichedFilters = {};
      Object.entries(newFilters).forEach(([key, filtro]) => {
        let filterDef = filtersFields.value.find((f) => f.field === filtro.field);
        let displayValue = filtro.displayValue;
        let label = filtro.label;

        if (!filterDef) {
          const dateFilter = filtersFields.value.find((f) => f.queryType === "date");
          if (dateFilter) {
            const dateOption = dateFilter.dateFieldOptions?.find(
              (opt) => opt.value === filtro.field
            );
            if (dateOption) {
              filterDef = dateFilter;
              label = dateOption.name;

              if (Array.isArray(filtro.value)) {
                let desdeFormatted = "";
                let hastaFormatted = "";

                filtro.value.forEach((op) => {
                  if (op.operator === ">=" && op.value) {
                    const date = new Date(op.value);
                    const day = String(date.getDate()).padStart(2, "0");
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const year = date.getFullYear();
                    desdeFormatted = `${day}-${month}-${year}`;
                  } else if (op.operator === "<=" && op.value) {
                    const date = new Date(op.value);
                    const day = String(date.getDate()).padStart(2, "0");
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const year = date.getFullYear();
                    hastaFormatted = `${day}-${month}-${year}`;
                  }
                });

                if (desdeFormatted && hastaFormatted) {
                  displayValue = `Desde ${desdeFormatted} Hasta ${hastaFormatted}`;
                } else if (desdeFormatted) {
                  displayValue = `Desde ${desdeFormatted}`;
                } else if (hastaFormatted) {
                  displayValue = `Hasta ${hastaFormatted}`;
                }
              }
            }
          }
        }

        // Enriquecer displayValue para dependencias
        if (filterDef?.type === "dropdown" && filterDef?.dependenciaOptions) {
          const selectedDependencia = filterDef.dependenciaOptions.find(
            (opt) => opt.id === filtro.value
          );
          displayValue = selectedDependencia?.dependencia || filtro.displayValue;
        }

        enrichedFilters[key] = {
          ...filtro,
          label: filterDef?.name || label || filtro.label,
          displayValue: displayValue,
        };
      });
      activeFilters.value = enrichedFilters;
    }
  },
  { deep: true }
);

watch(selectedField, () => {
  selectedValue.value = "";
  selectedDateField.value = null;
  filtros.value.fechaDesde = null;
  filtros.value.fechaHasta = null;
});
</script>

<style>
.filters-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.filters-row {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  width: 100%;
}

.filters-section-1 {
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  min-width: 250px;
}

.filters-section-2 {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1 1 auto;
  min-width: 200px;
  max-width: 250px;
}

.filters-section-3 {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex: 0 0 auto;
}

.filters-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  padding-top: 1rem;
}

.custom-action-button {
  display: flex;
  width: 124px;
  height: 36px;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  background-color: var(--Primary, #0b5195);
  color: white;
  border: none;
}

.custom-action-button:hover {
  opacity: 0.9;
  cursor: pointer;
}

.cancelar-button {
  display: flex;
  width: 124px;
  height: 36px;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  background: var(--White-White, #fff);
  color: #0b5195;
  border: none;
  box-shadow: 0px 1px 3px 0px rgba(166, 175, 195, 0.4);
}

.cancelar-button:hover {
  background: #f4f4f4 !important;
  color: #0b5195 !important;
  opacity: 0.9;
  cursor: pointer;
}

.blue-line {
  height: 4px;
  background-color: #0b5195;
  border-radius: 9999px;
}

.btn {
  display: flex;
  width: 124px;
  height: 36px;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  box-shadow: 0px 1px 3px rgba(166, 175, 195, 0.4);
  transition: 0.2s ease all;
  cursor: pointer;
  font-weight: 500;
}

.btn:hover {
  opacity: 0.9;
}

.btn-primary {
  background-color: #0b5195;
  color: white;
  border: 1px solid #0b5195;
}

.btn-primary:hover {
  background-color: #0b5195;
  color: #f4f4f4;
}

.btn-outline-primary {
  background-color: #fff;
  color: #0b5195;
  border: 1px solid #0b5195;
}

.btn-outline-primary:hover {
  background-color: #f4f4f4;
}

.btn-clear-chips {
  width: auto;
  min-width: 124px;
}

>>> .p-calendar .p-inputtext {
  border-radius: 6px !important;
}
</style>
